import { dataElementMapping } from '../PerformanceContext';

const PROGRAM_ID = 'yiT00XKrrHP';

const PROGRAM_STAGES = {
  OBJECTIVE: 'VIBtpvkpHFR', // Objectives program stage
  KRA: 'BJb3qTgbcTF',       // KRA program stage
  KPI: 'ivyCLjNLFrd'        // KPI program stage
};

const DATA_ELEMENTS = {
  // Objective fields
  OBJECTIVE_ID: 'plJovUlxfQ0',
  OBJECTIVE_NAME: 'EuN5ztFnXsQ',
  OBJECTIVE_DESCRIPTION: 'nWqW5BrcgP1',
  OBJECTIVE_WEIGHT: 'yfb9RCRpMB2',

  // KRA fields
  KRA_NAME: 'obapz6Gnyla',
  KRA_DESCRIPTION: 'DEECWPTj42E',
  KRA_WEIGHT: 'Xl4VEFoIFIw',

  // KPI fields
  KPI_NAME: 'DBmylDQA9KX',
  KPI_DESCRIPTION: 'rQqF75V9t7K',
  KPI_TARGET: 'Ru68Ax2Xo2L',

  LINKING_FIELD: 'plJovUlxfQ0' // This links KRAs/KPIs to their parent objective
};

// Robust event ID extraction
export const extractEventIdFromResponse = (response, isUpdate = false, existingId = null) => {
  if (isUpdate) return existingId;

  console.log('🔍 [ID DEBUG] Extracting event ID from:', response);

  try {
    if (response?.reference) return response.reference;
    if (response?.response?.reference) return response.response.reference;
    if (response?.response?.importSummaries?.[0]?.reference) return response.response.importSummaries[0].reference;
    if (response?.importSummaries?.[0]?.reference) return response.importSummaries[0].reference;
    if (response?.response?.imported?.[0]) return response.response.imported[0];
    if (response?.imported?.[0]) return response.imported[0];
    if (response?.response?.updated?.[0]) return response.response.updated[0];

    console.warn('❌ [ID DEBUG] Could not find event ID in response structure');
    console.warn('❌ [ID DEBUG] Response keys:', Object.keys(response));
    return null;
  } catch (error) {
    console.error('❌ [ID DEBUG] Error extracting event ID:', error);
    return null;
  }
};

// Fetch objective with all its KRAs and KPIs
export async function fetchObjectiveWithDetails(objectiveId, engine) {
  try {

    // Check if objectiveId looks valid
    if (!objectiveId || typeof objectiveId !== 'string' || objectiveId.length < 5) {
      console.warn('🟡 [fetchObjectiveWithDetails] Invalid objective ID format:', objectiveId);
      return { objective: null, kras: [], kpis: [] };
    }

    // Fetch the main objective with better error handling
    const objectiveResponse = await engine.query({
      objective: {
        resource: `events/${objectiveId}`,
        params: {
          fields: 'event,orgUnit,orgUnitName,eventDate,status,dataValues[dataElement,value]'
        }
      }
    }).catch(error => {
      // SPECIFIC ERROR HANDLING FOR 404
      if (error?.details?.httpStatusCode === 404) {
        console.warn('🟡 [fetchObjectiveWithDetails] Objective not found (404):', objectiveId);
        return { objective: null };
      }
      console.error('🟡 [fetchObjectiveWithDetails] Error fetching objective:', error);
      throw error; // Re-throw other errors
    });

    // CHECK IF OBJECTIVE EXISTS
    if (!objectiveResponse?.objective) {
      console.log('🟡 [fetchObjectiveWithDetails] Objective not found in response:', objectiveId);
      return { objective: null, kras: [], kpis: [] };
    }

    // Map the main objective
    const objectiveEvent = objectiveResponse.objective;
    const mappedObjective = mapEventToObjective(objectiveEvent);

    // PRESERVE RAW DATAVALUES FOR GOAL DETECTION
    mappedObjective.rawDataValues = objectiveEvent.dataValues || [];
    console.log('🎯 [GOAL FIX] Preserved raw dataValues:', {
      objectiveId: mappedObjective.id,
      dataValuesCount: mappedObjective.rawDataValues.length,
      dataElements: mappedObjective.rawDataValues.map(dv => dv.dataElement)
    });

    // Use the correct linking field for KRAs and KPIs
    const LINKING_FIELD = 'plJovUlxfQ0';

    // Fetch all KRA events linked to this objective with error handling
    const [kraEventsResponse, kpiEventsResponse] = await Promise.all([
      engine.query({
        kraEvents: {
          resource: 'events',
          params: {
            program: PROGRAM_ID,
            programStage: PROGRAM_STAGES.KRA,
            filter: [`${LINKING_FIELD}:eq:${objectiveId}`],
            fields: 'event,dataValues[dataElement,value]',
            pageSize: 1000
          }
        }
      }).catch(error => {
        console.warn('🟡 [fetchObjectiveWithDetails] Error fetching KRAs:', error);
        return { kraEvents: { events: [] } };
      }),

      engine.query({
        kpiEvents: {
          resource: 'events',
          params: {
            program: PROGRAM_ID,
            programStage: PROGRAM_STAGES.KPI,
            filter: [`${LINKING_FIELD}:eq:${objectiveId}`],
            fields: 'event,dataValues[dataElement,value]',
            pageSize: 1000
          }
        }
      }).catch(error => {
        console.warn('🟡 [fetchObjectiveWithDetails] Error fetching KPIs:', error);
        return { kpiEvents: { events: [] } };
      })
    ]);

    // Map KRAs and KPIs with detailed debugging
    const kraEvents = kraEventsResponse.kraEvents?.events || [];
    const kpiEvents = kpiEventsResponse.kpiEvents?.events || [];

    console.log('🟡 [DEBUG] KRA events found:', kraEvents.length);
    console.log('🟡 [DEBUG] KPI events found:', kpiEvents.length);

    // Enhanced debugging for events
    if (kraEvents.length > 0) {
      kraEvents.forEach(event => {
        const linkingValue = event.dataValues?.find(dv => dv.dataElement === LINKING_FIELD)?.value;
        console.log('🟡 [KRA DEBUG] Event:', event.event, 'Links to objective:', linkingValue, 'Expected:', objectiveId);
      });
    }

    if (kpiEvents.length > 0) {
      kpiEvents.forEach(event => {
        const linkingValue = event.dataValues?.find(dv => dv.dataElement === LINKING_FIELD)?.value;
        console.log('🟡 [KPI DEBUG] Event:', event.event, 'Links to objective:', linkingValue, 'Expected:', objectiveId);
      });
    }

    const kras = kraEvents.map(event => {
      const mappedKra = mapEventToKra(event);
      console.log('🟡 [DEBUG] Mapped KRA:', {
        eventId: event.event,
        mappedId: mappedKra.id,
        name: mappedKra.name,
        linksTo: event.dataValues?.find(dv => dv.dataElement === LINKING_FIELD)?.value
      });
      return mappedKra;
    });

    const kpis = kpiEvents.map(event => {
      const mappedKpi = mapEventToKpi(event);
      console.log('🟡 [DEBUG] Mapped KPI:', {
        eventId: event.event,
        mappedId: mappedKpi.id,
        name: mappedKpi.name,
        linksTo: event.dataValues?.find(dv => dv.dataElement === LINKING_FIELD)?.value
      });
      return mappedKpi;
    });

    console.log('🟡 [fetchObjectiveWithDetails] Final results:', {
      objectiveId: mappedObjective.id,
      objectiveName: mappedObjective.name,
      kras: kras.length,
      kpis: kpis.length,
      kraEventsCount: kraEvents.length,
      kpiEventsCount: kpiEvents.length,
      hasRawDataValues: !!mappedObjective.rawDataValues // ✅ NEW: Check if we have raw data
    });

    return {
      objective: mappedObjective,
      kras,
      kpis
    };

  } catch (error) {
    // ✅ IMPROVED ERROR HANDLING
    if (error?.details?.httpStatusCode === 404) {
      console.warn('🟡 [fetchObjectiveWithDetails] Objective not found (404 catch):', objectiveId);
    } else {
      console.error('❌ Error in fetchObjectiveWithDetails:', error);
    }
    return { objective: null, kras: [], kpis: [] };
  }
}

// Save objective, KRA, or KPI
export async function saveObjectiveEvent(eventData, engine, isUpdate = false, type = 'objective') {
  console.log('🔍 [API DEBUG] saveObjectiveEvent called for type:', type);
  console.log('🔍 [API DEBUG] Operation:', isUpdate ? 'UPDATE' : 'CREATE');
  console.log('🔍 [API DEBUG] Event data:', JSON.stringify(eventData, null, 2));

  try {
    const mutation = {
      type: isUpdate ? 'update' : 'create',
      resource: isUpdate ? `events/${eventData.event}` : 'events',
      data: eventData
    };

    console.log('🔍 [API DEBUG] Mutation object:', mutation);

    const startTime = Date.now();
    const response = await engine.mutate(mutation);
    const responseTime = Date.now() - startTime;


    // Enhanced response parsing with multiple fallbacks
    let eventId;
    let parsedResponse = response;

    // Handle different response structures
    if (isUpdate) {
      eventId = eventData.event;
    } else {
      // Multiple fallback strategies for extracting event ID from create response
      eventId = response?.response?.uid ||
        response?.response?.importSummaries?.[0]?.reference ||
        response?.imported ||
        response?.response?.imported ||
        response?.reference;

      // If still not found, try to parse from status response
      if (!eventId && response?.response?.status === 'SUCCESS') {
        // Sometimes the ID is in the description or other fields
        const match = response?.response?.description?.match(/Event\s+([a-zA-Z0-9]+)/);
        if (match) eventId = match[1];
      }
    }

    console.log('🔍 [API DEBUG] Extracted event ID:', eventId);

    // If we still don't have an event ID for create operations, this is critical
    if (!isUpdate && !eventId) {
      console.warn('⚠️ [API DEBUG] Could not extract event ID from create response:', response);
      // In some cases, we might need to parse the raw response
      try {
        // Fallback to the original extractEventIdFromResponse logic if available
        if (typeof extractEventIdFromResponse === 'function') {
          eventId = extractEventIdFromResponse(response, isUpdate, eventData.event);
          console.log('🔍 [API DEBUG] Event ID from fallback function:', eventId);
        }
      } catch (fallbackError) {
        console.error('❌ [API DEBUG] Fallback extraction failed:', fallbackError);
      }
    }

    // Comprehensive success logging
    console.log('✅ [API DEBUG] Request completed successfully', {
      operation: isUpdate ? 'UPDATE' : 'CREATE',
      type,
      eventId,
      responseTime: responseTime + 'ms'
    });

    return {
      success: true,
      eventId,
      response: parsedResponse,
      responseTime
    };

  } catch (error) {
    console.error('❌ [API DEBUG] Error in saveObjectiveEvent:', error);

    // Enhanced error diagnostics - capture full DHIS2 response
    const errorDetails = {
      type,
      isUpdate,
      operation: isUpdate ? 'UPDATE' : 'CREATE',
      timestamp: new Date().toISOString()
    };

    if (error.details) {
      console.error('❌ [API DEBUG] Error details:', JSON.stringify(error.details, null, 2));
      errorDetails.details = error.details;
    }

    if (error.response) {
      console.error('❌ [API DEBUG] Error response:', JSON.stringify(error.response, null, 2));
      errorDetails.apiResponse = error.response;
    }

    if (error.message) {
      console.error('❌ [API DEBUG] Error message:', error.message);
      errorDetails.message = error.message;
    }

    // Try to extract DHIS2 import summary if available
    if (error.response?.response?.importSummaries) {
      console.error('❌ [API DEBUG] DHIS2 Import Summaries:', JSON.stringify(error.response.response.importSummaries, null, 2));
      const importSummary = error.response.response.importSummaries[0];
      if (importSummary?.conflicts) {
        console.error('❌ [API DEBUG] Conflicts:', JSON.stringify(importSummary.conflicts, null, 2));
        errorDetails.conflicts = importSummary.conflicts;
      }
      if (importSummary?.description) {
        console.error('❌ [API DEBUG] Import description:', importSummary.description);
        errorDetails.importDescription = importSummary.description;
      }
    }

    // Try to extract error from response body
    if (error.response && error.response.status === 409) {
      console.error('❌ [API DEBUG] 409 Conflict detected - checking for validation errors');
      // The 409 might have a response body with details
      if (error.response._bodyText) {
        try {
          const body = JSON.parse(error.response._bodyText);
          console.error('❌ [API DEBUG] 409 Response body:', JSON.stringify(body, null, 2));
          errorDetails.conflictBody = body;
        } catch (e) {
          console.error('❌ [API DEBUG] Could not parse 409 response body:', error.response._bodyText);
          errorDetails.conflictBodyRaw = error.response._bodyText;
        }
      }
    }

    // Preserve the original error handling behavior
    const errorMessage = error.details?.message || error.message || 'Unknown error occurred';

    // Re-throw with enhanced information
    const enhancedError = new Error(`Failed to ${isUpdate ? 'update' : 'create'} ${type}: ${errorMessage}`);
    enhancedError.details = errorDetails;
    enhancedError.originalError = error;

    throw enhancedError;
  }
}


export const mapEventToObjective = (event) => {
  console.log('🟡 [mapEventToObjective] Raw event:', event);

  // ✅ DEBUG: Check for alignment data specifically
  const alignmentDataValue = event.dataValues?.find(dv => dv.dataElement === 'neuilg3L4d2');
  const goalAlignmentDataValue = event.dataValues?.find(dv => dv.dataElement === 'VhrIQQIVpB9');

  console.log('🟡 [DEBUG ALIGNMENT] Specific alignment check:', {
    objectiveAlignmentElement: 'neuilg3L4d2',
    foundAlignmentValue: alignmentDataValue?.value,
    companyAlignedGoalElement: 'VhrIQQIVpB9',
    foundGoalValue: goalAlignmentDataValue?.value,
    allDataValues: event.dataValues?.map(dv => ({
      dataElement: dv.dataElement,
      value: dv.value,
      isAlignment: dv.dataElement === 'neuilg3L4d2',
      isGoalAlignment: dv.dataElement === 'VhrIQQIVpB9'
    }))
  });

  const mapped = {
    id: event.event,
    eventId: event.event,
    orgUnit: event.orgUnit,
    trackedEntityInstance: event.trackedEntityInstance,
    enrollment: event.enrollment
  };

  // ✅ SIMPLIFIED MAPPING - Use direct dataElementMapping
  console.log('🟡 [mapEventToObjective] Using dataElementMapping:', dataElementMapping);

  event.dataValues?.forEach(dv => {
    // Find the field name by data element ID
    const fieldName = Object.keys(dataElementMapping).find(
      key => dataElementMapping[key] === dv.dataElement
    );

    // DEBUG: Check if remarks data element is present
    if (dv.dataElement === 'KeGWxm1IDYF') {
      console.log('🟡 [REMARKS DEBUG] Found remarks data element:', {
        dataElement: dv.dataElement,
        value: dv.value,
        isEmpty: !dv.value
      });
    }

    if (fieldName) {
      mapped[fieldName] = convertValue(fieldName, dv.value);
      console.log('🟡 [mapEventToObjective] Mapped field:', {
        dataElement: dv.dataElement,
        fieldName,
        value: dv.value,
        convertedValue: mapped[fieldName]
      });
    } else {
      console.log('🟡 [mapEventToObjective] Unmapped data element:', {
        dataElement: dv.dataElement,
        value: dv.value
      });
    }
  });

  // ✅ CRITICAL: Ensure alignment fields are explicitly set
  // If not found in dataValues, set them to empty strings
  if (mapped.objectiveAlignment === undefined) {
    mapped.objectiveAlignment = '';
    console.log('🟡 [mapEventToObjective] objectiveAlignment not found, setting to empty string');
  }

  if (mapped.companyAlignedGoal === undefined) {
    mapped.companyAlignedGoal = '';
    console.log('🟡 [mapEventToObjective] companyAlignedGoal not found, setting to empty string');
  }

  console.log('🟡 [mapEventToObjective] Final mapped objective:', {
    id: mapped.id,
    name: mapped.name,
    remarks: mapped.remarks, // DEBUG: Check remarks value
    remarksFound: mapped.remarks && mapped.remarks.length > 0, // DEBUG
    objectiveAlignment: mapped.objectiveAlignment,
    companyAlignedGoal: mapped.companyAlignedGoal,
    hasObjectiveAlignment: !!mapped.objectiveAlignment,
    hasCompanyAlignedGoal: !!mapped.companyAlignedGoal
  });

  return mapped;
};


const mapEventToKra = (event) => {
  const mapped = { id: event.event };
  const kraMappings = {
    [DATA_ELEMENTS.KRA_NAME]: 'name',
    [DATA_ELEMENTS.KRA_DESCRIPTION]: 'description',
    [DATA_ELEMENTS.KRA_WEIGHT]: 'weight'
  };

  event.dataValues?.forEach(dv => {
    const fieldName = kraMappings[dv.dataElement];
    if (fieldName) {
      mapped[fieldName] = convertValue(fieldName, dv.value);
    }
  });
  return mapped;
};

const mapEventToKpi = (event) => {
  const mapped = { id: event.event };
  const kpiMappings = {
    [DATA_ELEMENTS.KPI_NAME]: 'name',
    [DATA_ELEMENTS.KPI_DESCRIPTION]: 'description',
    [DATA_ELEMENTS.KPI_TARGET]: 'target'
  };

  event.dataValues?.forEach(dv => {
    const fieldName = kpiMappings[dv.dataElement];
    if (fieldName) {
      mapped[fieldName] = convertValue(fieldName, dv.value);
    }
  });
  return mapped;
};



// Helper function to convert values based on field type
const convertValue = (fieldName, value) => {
  if (fieldName.includes('weight') || fieldName.includes('target') || fieldName.includes('progress')) {
    return Number(value) || 0;
  }
  return value || '';
};


// Create event payload for different types - Updated to use position TEI
export const createEventPayload = (type, data, objectiveId, orgUnitId, positionTeiId, enrollmentId) => {
  console.log('🔍 [PAYLOAD DEBUG] createEventPayload called');
  console.log('🔍 [PAYLOAD DEBUG] Parameters:', {
    type,
    data,
    objectiveId,
    orgUnitId,
    positionTeiId: positionTeiId || 'NULL',
    enrollmentId: enrollmentId || 'NULL'
  });

  // Determine the correct program stage based on type
  const programStage = type === 'objective'
    ? PROGRAM_STAGES.OBJECTIVE
    : type === 'kra'
      ? PROGRAM_STAGES.KRA
      : PROGRAM_STAGES.KPI;

  // ✅ CRITICAL FIX: Only include trackedEntityInstance if it's a VALID TEI ID
  const baseEvent = {
    program: PROGRAM_ID,
    programStage: programStage,
    orgUnit: orgUnitId,
    eventDate: new Date().toISOString().split('T')[0],
    status: 'ACTIVE',
    dataValues: []
  };

  // ✅ Only add trackedEntityInstance if positionTeiId is valid (not null/undefined/'NULL')
  if (positionTeiId && positionTeiId !== 'NULL' && positionTeiId !== 'undefined' && typeof positionTeiId === 'string' && positionTeiId.length > 5) {
    baseEvent.trackedEntityInstance = positionTeiId;
    console.log('✅ [PAYLOAD] Added valid trackedEntityInstance:', positionTeiId);
  } else {
    console.warn('⚠️ [PAYLOAD] Skipping trackedEntityInstance - invalid value:', positionTeiId);
  }
  
  // ✅ Only add enrollment if it's valid
  if (enrollmentId && enrollmentId !== 'NULL' && enrollmentId !== 'undefined' && typeof enrollmentId === 'string' && enrollmentId.length > 5) {
    baseEvent.enrollment = enrollmentId;
    console.log('✅ [PAYLOAD] Added valid enrollment:', enrollmentId);
  }

  //  Complete mapping with all field names that match your objective data structure
  const typeMappings = {
    objective: {
      // Core fields
      name: DATA_ELEMENTS.OBJECTIVE_NAME,
      description: DATA_ELEMENTS.OBJECTIVE_DESCRIPTION,
      weight: DATA_ELEMENTS.OBJECTIVE_WEIGHT,
      
      //  These need to match what you set in updateObjectiveField
      objectiveAlignment: 'neuilg3L4d2',          
      higherLevelObjectiveId: 'neuilg3L4d2',     
      companyAlignedGoal: 'VhrIQQIVpB9',         
      linkedGoalId: 'VhrIQQIVpB9',               
      
      // Role and position
      objectiveRoleAssigned: 'vFoMLF5eZ5X',       // Role assigned
      
      // Dates
      performanceYear: 'CVaNcskWvmN',
      startDate: 'eDcVy661j9e',
      completionDate: 'J0rjxrDKn1l',
      
      // Remarks and other fields
      remarks: 'KeGWxm1IDYF',
      workType: 'wX6yZ7aB8cD9',
      
      // Display fields (not saved to DHIS2 but used in UI)
      // These will be filtered out because they're not in the mapping
    },
    kra: {
      name: DATA_ELEMENTS.KRA_NAME,
      description: DATA_ELEMENTS.KRA_DESCRIPTION,
      weight: DATA_ELEMENTS.KRA_WEIGHT,
      // KRAs also need alignment to parent objective
      objectiveAlignment: 'neuilg3L4d2',
    },
    kpi: {
      name: DATA_ELEMENTS.KPI_NAME,
      description: DATA_ELEMENTS.KPI_DESCRIPTION,
      target: DATA_ELEMENTS.KPI_TARGET,
    }
  };

  const mapping = typeMappings[type];
  if (mapping) {
    Object.entries(mapping).forEach(([fieldName, dataElementId]) => {
      // Check multiple possible field names for the same data element
      let fieldValue = data[fieldName];
      
      // For alignment fields, also check alternative field names
      if (dataElementId === 'neuilg3L4d2') {
        // This is a parent objective link - check multiple possible field names
        fieldValue = fieldValue || 
                    data.objectiveAlignment || 
                    data.higherLevelObjectiveId ||
                    data.parentObjectiveId;
      }
      
      if (dataElementId === 'VhrIQQIVpB9') {
        // This is a company goal link - check multiple possible field names
        fieldValue = fieldValue || 
                    data.companyAlignedGoal || 
                    data.linkedGoalId ||
                    data.goalId;
      }
      
      // Filter out null, undefined, AND empty strings for DHIS2 compatibility
      if (fieldValue !== null && fieldValue !== undefined && fieldValue !== '') {
        baseEvent.dataValues.push({
          dataElement: dataElementId,
          value: String(fieldValue)
        });
        console.log(`🔍 [PAYLOAD DEBUG] Added ${fieldName} (${dataElementId}):`, fieldValue);
      } else {
        console.log(`🔍 [PAYLOAD DEBUG] Skipping ${fieldName} (empty):`, { fieldValue });
      }
    });
  }

  // 🔗 Link to parent objective for KRAs and KPIs
  if (type !== 'objective' && objectiveId) {
    baseEvent.dataValues.push({
      dataElement: DATA_ELEMENTS.LINKING_FIELD,
      value: objectiveId
    });
    console.log(`🔍 [PAYLOAD DEBUG] Added linking field for ${type}:`, objectiveId);
  }

  // Link objective to its Position TEI via SlgO60cMK3F
  if (type === 'objective' && positionTeiId && positionTeiId !== 'NULL' && positionTeiId !== 'undefined') {
    // Check if we already added this (avoid duplicates)
    const hasPositionLink = baseEvent.dataValues.some(dv => dv.dataElement === 'SlgO60cMK3F');
    if (!hasPositionLink) {
      baseEvent.dataValues.push({
        dataElement: 'SlgO60cMK3F', // Position Staff ID (stores Position TEI ID)
        value: positionTeiId
      });
      console.log('✅ Added Position TEI linkage:', { dataElement: 'SlgO60cMK3F', value: positionTeiId });
    }
  }

  // Log summary of what's being saved
  console.log('🔍 [PAYLOAD DEBUG] Final payload summary:', {
    type,
    objectiveId: objectiveId || 'new',
    dataValueCount: baseEvent.dataValues.length,
    dataElements: baseEvent.dataValues.map(dv => dv.dataElement),
    hasTrackedEntityInstance: !!baseEvent.trackedEntityInstance,
    trackedEntityInstance: baseEvent.trackedEntityInstance
  });

  return baseEvent;
};

export const deleteObjective = {
  resource: 'event',
  type: 'delete',
  id: ({ id }) => id
}
